import {
  BadgeCheck,
  Clock,
  AlertTriangle,
  Send,
  MessageSquare,
  CalendarDays,
  Hourglass,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useState } from "react";

export const ScheduleList = ({
  status,
  messages,
  hasMore,
  totalCount,
  onPaginate,
  historyStacks,
}) => {
  const [loading, setLoading] = useState(false);

  const pageSize = 10;
  const currentPage = historyStacks.length;
  const start = currentPage * pageSize + 1;
  const end = Math.min(start + messages.length - 1, totalCount);

  const handleNext = async () => {
    if (!hasMore) return;
    setLoading(true);
    await onPaginate("next");
    setLoading(false);
  };

  const handlePrev = async () => {
    if (historyStacks.length === 0) return;
    setLoading(true);
    await onPaginate("prev");
    setLoading(false);
  };

  const statusIcon = (status) => {
    switch (status) {
      case "sent":
        return <BadgeCheck className="text-emerald-600" size={16} />;
      case "pending":
        return <Hourglass className="text-amber-500" size={16} />;
      case "failed":
        return <AlertTriangle className="text-red-500" size={16} />;
      default:
        return null;
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case "sent":
        return "text-emerald-700 bg-emerald-50 border-emerald-200";
      case "pending":
        return "text-amber-700 bg-amber-50 border-amber-200";
      case "failed":
        return "text-red-700 bg-red-50 border-red-200";
      default:
        return "text-gray-700 bg-gray-50 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  if (messages.length === 0) {
    return (
      <div
        className="flex flex-col border border-blue-200 rounded-2xl bg-white overflow-hidden"
        style={{ boxShadow: "0px 4px 24px rgba(67, 98, 193, 0.12)" }}
      >
        <div className="flex flex-col items-center justify-center py-16 px-4">
          <div className="p-4 bg-blue-100 rounded-full mb-4">
            <MessageSquare className="text-blue-500" size={40} />
          </div>
          <p className="text-blue-700 text-lg font-semibold">
            No messages found
          </p>
          <p className="text-gray-500 text-base mt-1">
            Your {status} messages will appear here
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col border border-blue-200 rounded-2xl bg-white overflow-hidden"
      style={{ boxShadow: "0px 4px 24px rgba(67, 98, 193, 0.12)" }}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-base text-gray-700">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left font-semibold">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-gray-500" />
                  Template ID
                </div>
              </th>
              <th className="px-6 py-4 text-left font-semibold">
                <div className="flex items-center gap-2">
                  <Clock size={16} className="text-gray-500" />
                  Status
                </div>
              </th>
              <th className="px-6 py-4 text-left font-semibold">
                <div className="flex items-center gap-2">
                  <Send size={16} className="text-gray-500" />
                  {status === "pending" ? "Scheduled For" : "Delivered At"}
                </div>
              </th>
              <th className="px-6 py-4 text-left font-semibold">
                <div className="flex items-center gap-2">
                  <CalendarDays size={16} className="text-gray-500" />
                  Created At
                </div>
              </th>
              <th className="px-6 py-4 text-left font-semibold">
                <div className="flex items-center gap-2">
                  <MessageSquare size={16} className="text-gray-500" />
                  Recipient
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {messages.map((item) => (
              <tr
                key={item._id}
                className="hover:bg-blue-50 transition-colors duration-150"
              >
                <td className="px-6 py-4">
                  <div className="font-mono text-base font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg inline-block">
                    {item.messageTemplateId}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-medium text-sm ${statusColor(
                      item.status
                    )}`}
                  >
                    {statusIcon(item.status)}
                    <span className="capitalize">{item.status}</span>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-600 font-medium">
                  {item.status === "sent" && item.sentAt
                    ? formatDate(item.sentAt)
                    : item.status === "pending"
                    ? formatDate(item.sentAt)
                    : "-"}
                </td>
                <td className="px-6 py-4 text-gray-600 font-medium">
                  {formatDate(item.createdAt)}
                </td>
                <td className="px-6 py-4">
                  {item.groupName ? (
                    <span className="text-blue-700 font-semibold">
                      {item.groupName}
                    </span>
                  ) : (
                    <span className="text-gray-700 font-medium">
                      {item.phone}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
        <div className="text-sm text-gray-600">
          Showing <span className="font-semibold">{start}</span>–
          <span className="font-semibold">{end}</span> of{" "}
          <span className="font-semibold">{totalCount}</span> total messages
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handlePrev}
            disabled={historyStacks.length === 0 || loading}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              historyStacks.length === 0 || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm"
            }`}
          >
            <ChevronLeft size={16} />
            Previous
          </button>

          <button
            onClick={handleNext}
            disabled={!hasMore || loading}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              !hasMore || loading
                ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 shadow-sm"
            }`}
          >
            Next
            <ChevronRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};
